import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lists in the director's approved order (list 4 retired 11 Oct 2026 - nothing stores a test password any more)
const LISTS = [
  { item: 'disable', key: '1_disable_test_accounts', sub: 'accounts', title: 'List 1 — disable @insul.com test accounts', change: 'sets disabled_at / disabled_reason / disabled_by on each account; nothing deleted, password hash untouched, one evidence row per account' },
  { item: 'archive_markers', key: '2_archived_password_markers', sub: 'records', title: 'List 2 — archived-password markers', change: 'replaces each password hash in merged_users_archive with the [STRIPPED] marker + <field>_stripped_at' },
  { item: 'placeholder_rename', key: '3_placeholder_rename', sub: 'records', title: 'List 3 — placeholder rename to .invalid', change: 'renames recovered_…@insul.com placeholders to @insul.invalid; only the email field changes' },
  { item: 'jobrun_markers', key: '5_jobrun_summary_hash_markers', sub: 'records', title: 'List 5 — password hashes inside old job_runs rows', change: 'replaces each hash in old job_runs summaries with the [STRIPPED] marker' },
  { item: 'demo_account', key: '6_demo_account', sub: 'accounts', title: 'List 6 — demo@insulnutrients.com account', change: 'sets disabled_at / disabled_reason / disabled_by on the demo account only; nothing deleted, no purge clock, one evidence row (director 11 Oct)' },
];

const Row = ({ r }) => (
  <li className="font-mono text-xs py-0.5 border-t border-border/40 flex flex-wrap gap-x-3">
    <span className="font-bold">{r.id_prefix}</span>
    {r.masked_email && <span>{r.masked_email}</span>}
    {r.created_at && <span>created {r.created_at}</span>}
    {r.holds && <span>holds: meals {r.holds.meals} · photos {r.holds.photos} · backups {String(r.holds.backups)}</span>}
    {r.legacy_is_admin_flag && <span className="text-amber-700">legacy is_admin flag</span>}
    {r.fields && <span>fields: {r.fields.join(', ')}</span>}
    {r.collection && <span>{r.collection}</span>}
    {r.flag && <span className="text-red-700">{r.flag}</span>}
  </li>
);

const ListCard = ({ def, data, dryRunId, token, onApplied }) => {
  const [reason, setReason] = useState('');
  const [approved, setApproved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const rows = (data && data[def.sub]) || [];
  const flagged = data?.flagged_possible_real_person || [];
  const already = data?.already_disabled || [];
  const apply = async () => {
    setBusy(true); setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/cleanup/step5/apply`,
        { dry_run_id: dryRunId, item: def.item, approved: true, reason, expected_count: rows.length },
        { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data); toast.success(`${def.title}: ${res.data.done} applied`); onApplied();
    } catch (e) {
      const d = e.response?.data?.detail; setResult({ refused: typeof d === 'string' ? d : JSON.stringify(d) }); toast.error('Apply refused — nothing changed');
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-border/60 p-4" data-testid={`step5-list-${def.item}`}>
      <p className="font-semibold">{def.title} — <span data-testid={`step5-count-${def.item}`}>{data ? data.count : '…'}</span> candidate{data?.count === 1 ? '' : 's'}</p>
      <p className="text-xs text-muted-foreground">Apply would: {def.change}</p>
      {rows.length > 0 && <ul className="mt-2 bg-white/70 rounded p-2">{rows.map((r, i) => <Row key={i} r={r} />)}</ul>}
      {flagged.length > 0 && (
        <div className="mt-2 rounded border border-red-300 bg-red-50 p-2" data-testid={`step5-flagged-${def.item}`}>
          <p className="text-xs font-bold text-red-800">Flagged — possible real person, NOT on the apply list; your decision:</p>
          <ul>{flagged.map((r, i) => <Row key={i} r={r} />)}</ul>
        </div>
      )}
      {already.length > 0 && <p className="text-xs mt-2 text-muted-foreground">Already disabled: {already.map((r) => r.id_prefix).join(', ')}</p>}
      {rows.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (at least 10 characters) — recorded on every evidence row" className="border rounded-full px-3 py-1 text-sm flex-1 min-w-[260px]" data-testid={`step5-reason-${def.item}`} />
          <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} data-testid={`step5-approve-${def.item}`} /> I approve exactly these {rows.length} record{rows.length === 1 ? '' : 's'} from dry run {String(dryRunId).slice(0, 8)}</label>
          <Button size="sm" variant="destructive" className="rounded-full" disabled={busy || !approved || reason.trim().length < 10} onClick={apply} data-testid={`step5-apply-${def.item}`}>{busy ? 'Applying…' : `Apply list (${rows.length})`}</Button>
        </div>
      )}
      {result && <pre className="text-[11px] mt-2 bg-white/70 rounded p-2 overflow-x-auto" data-testid={`step5-result-${def.item}`}>{JSON.stringify(result, null, 1).slice(0, 1500)}</pre>}
    </div>
  );
};

export const Step5Panel = ({ token }) => {
  const [dry, setDry] = useState(null);
  const [busy, setBusy] = useState(false);
  const runDry = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/cleanup/step5/dry-run`, { headers: { Authorization: `Bearer ${token}` } });
      setDry(res.data); toast.success('Dry run complete — nothing changed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Dry run failed'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="step5-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-bold text-lg">Step 5 clean-up</h3>
          <p className="text-xs text-muted-foreground">Dry run is read-only (it only stores the list it showed you under a dry_run_id). Apply one list at a time; it refuses if the live records differ from the dry run you approved. List 4 (test passwords) is retired.</p>
        </div>
        <Button onClick={runDry} disabled={busy} className="rounded-full" data-testid="step5-dry-run-btn">{busy ? 'Running…' : dry ? 'Run dry run again' : 'Dry run'}</Button>
      </div>
      {dry && (
        <div className="mt-3 space-y-3">
          <p className="text-xs font-mono" data-testid="step5-dry-run-id">environment {dry.environment} · dry_run_id {dry.dry_run_id}</p>
          {LISTS.map((def) => <ListCard key={def.item} def={def} data={dry[def.key]} dryRunId={dry.dry_run_id} token={token} onApplied={runDry} />)}
        </div>
      )}
    </div>
  );
};
